/**
 * DesignSystemDoRepositorio — a opção PRINCIPAL do Design System em projeto existente.
 *
 * Pedido do dono (2026-10-05): em brownfield com escopo fullstack/frontend, o Design Studio
 * carrega o Design System que JÁ ESTÁ no repositório indexado. Este painel vive dentro do
 * cartão "Design System" (as outras opções continuam lá, como alternativa) e mostra:
 *   - de onde veio: repositório, branch e commit do índice (e o estado do grafo);
 *   - a COBERTURA: arquivos lidos, fontes de token encontradas, o que NÃO foi encontrado,
 *     o que não deu para resolver sem executar código, tetos aplicados;
 *   - a origem de cada token (arquivo, linha e trecho) e o inventário de componentes;
 *   - "Extrair de novo" (depois de reindexar), "Ajustar tokens" e, quando outra opção está
 *     em uso, "Usar o Design System do repositório".
 *
 * Ao abrir, se o projeto ainda não tem Design System nem extração anterior, a extração
 * dispara sozinha (`extrairAoAbrir`). Repositório sem front ou sem token extraível diz isso
 * e deixa o cartão nas opções de sempre.
 *
 * Estilos inline com a paleta `C`; as cores mostradas nas amostras são os VALORES do token.
 * a11y: estados em aria-live, listas longas em <details>, rótulo em todo campo.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { C } from '@/theme/palette';
import { Btn } from '@/components/ui';
import { useActivateDesignSystem } from '@/api/designSystem';
import {
  useDesignSystemDoRepositorio, useExtrairDoRepositorio, useAjustarTokens,
} from '@/api/designSystemRepositorio';

const GRUPO_LABEL = {
  color: 'Cores', typography: 'Tipografia', space: 'Espaçamento', radius: 'Raios', shadow: 'Sombras',
};
const GRUPOS = ['color', 'typography', 'space', 'radius', 'shadow'];

const CATEGORIA_LABEL = {
  botoes: 'Botões', campos: 'Campos de formulário', cartoes: 'Cartões', layout: 'Layout',
  navegacao: 'Navegação', feedback: 'Feedback', dados: 'Dados', outros: 'Outros',
};

const n = (v) => Number(v ?? 0).toLocaleString('pt-BR');

function mensagem(err) {
  if (!err) return '';
  const detail = err.detail ?? err.problemBody?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err.status === 403) return 'Você não tem permissão para alterar o Design System deste projeto.';
  return 'Não foi possível concluir a operação com o Design System do repositório.';
}

function dataCurta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
}

const etiqueta = (cor) => ({
  fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
  background: `${cor}22`, color: cor,
});
const lista = { margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55 };
const subtitulo = { fontSize: 13, color: C.neutral, fontWeight: 700 };

// ─── De onde veio ─────────────────────────────────────────────────────────────

function Indice({ repositorios }) {
  const usados = (repositorios ?? []).filter((r) => r.usado !== false);
  if (!usados.length) return null;
  return (
    <div data-testid="ds-repo-indice" style={{ marginTop: 10 }}>
      <span style={subtitulo}>De onde veio</span>
      <ul style={{ ...lista, color: C.neutralDim }}>
        {usados.map((r) => {
          const commit = String(r.commit || '').slice(0, 7);
          const grafo = r.grafo ?? {};
          const grafoCommit = String(grafo.commit || '').slice(0, 7);
          return (
            <li key={r.id}>
              <strong style={{ color: C.neutral }}>{r.repo}</strong>
              {' · '}branch <code>{r.branch || 'não informada'}</code>
              {' · '}commit <code>{commit || 'não registrado'}</code>
              {dataCurta(r.indexadoEm) ? ` · indexado em ${dataCurta(r.indexadoEm)}` : ' · sem data de indexação'}
              {' · '}grafo {grafo.estado === 'pronto'
                ? `pronto${grafoCommit ? ` (commit ${grafoCommit})` : ''}`
                : (grafo.estado || 'ausente')}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Cobertura ────────────────────────────────────────────────────────────────

function Cobertura({ cobertura }) {
  if (!cobertura) return null;
  const fontes = cobertura.fontes ?? [];
  const faltas = cobertura.naoEncontrado ?? [];
  const papeis = cobertura.papeisFaltando ?? [];
  const pendentes = cobertura.naoResolvidosExemplos ?? [];
  const incompletos = cobertura.arquivosIncompletos ?? [];
  const tetos = cobertura.tetos ?? [];
  const ia = cobertura.ia ?? {};
  const pelaIa = cobertura.papeisPelaIa ?? [];
  return (
    <div data-testid="ds-repo-cobertura" style={{ marginTop: 12 }}>
      <span style={subtitulo}>Cobertura</span>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: C.neutralDim }}>
        {n(cobertura.arquivosLidos)} arquivo(s) de tema/tokens lido(s) por inteiro
        {' · '}{n(cobertura.arquivosDeFront)} arquivo(s) de front-end no índice
        {' · '}{n(cobertura.tokensNoDesignSystem)} token(s) no Design System
        {(cobertura.superficies ?? []).length > 1
          ? ` · superfícies: ${cobertura.superficies.join(' e ')} (principal: ${cobertura.superficiePrincipal})`
          : ''}
      </p>

      {fontes.length > 0 && (
        <ul aria-label="Fontes de token encontradas" style={{ ...lista, color: C.neutral }}>
          {fontes.map((f) => (
            <li key={f.fonte}>
              {f.rotulo} — {n(f.tokens)} token(s)
              <span style={{ color: C.neutralMuted }}> · {(f.arquivos ?? []).join(', ')}</span>
            </li>
          ))}
        </ul>
      )}

      {(faltas.length > 0 || papeis.length > 0) && (
        <div role="status" aria-live="polite"
          style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${C.warn}66`, background: 'rgba(255,176,32,0.08)' }}>
          <strong style={{ color: C.warn, fontSize: 12.5 }}>Não encontrado no repositório</strong>
          <ul aria-label="Não encontrado no repositório" style={{ ...lista, color: C.neutral }}>
            {faltas.map((f) => <li key={f}>{f}</li>)}
            {papeis.length > 0 && (
              <li>papéis de cor sem token: {papeis.join(', ')} — nenhum valor foi inventado</li>
            )}
          </ul>
        </div>
      )}

      <ul style={{ ...lista, color: C.neutralDim }}>
        {pelaIa.length > 0 && (
          <li>Papéis indicados pela IA (apontando um token do repositório): {pelaIa.join(', ')}</li>
        )}
        {ia.usada === false && ia.motivo && <li>Consolidação por IA: {ia.motivo}</li>}
        {Number(cobertura.temaEscuro) > 0 && (
          <li>{n(cobertura.temaEscuro)} token(s) de tema escuro encontrados — ficam fora do tema base</li>
        )}
        {Number(cobertura.variaveisDeComponente) > 0 && (
          <li>{n(cobertura.variaveisDeComponente)} variável(is) de componente — não são tokens do sistema</li>
        )}
        {Number(cobertura.descartadosSemOrigem) > 0 && (
          <li>{n(cobertura.descartadosSemOrigem)} token(s) descartado(s) por não ter origem</li>
        )}
        {incompletos.map((a) => (
          <li key={a.arquivo} style={{ color: C.warn }}>
            {a.arquivo}: pedaço(s) {(a.pedacosFaltando ?? []).join(', ')} faltando no índice — reindexe
          </li>
        ))}
        {tetos.map((t, i) => (
          <li key={i} style={{ color: C.warn }}>
            Teto aplicado: {t.oQue} — {n(t.limite)} de {n(t.total)}
          </li>
        ))}
      </ul>

      {Number(cobertura.naoResolvidos) > 0 && (
        <details style={{ marginTop: 6, fontSize: 12.5, color: C.neutralDim }}>
          <summary style={{ cursor: 'pointer' }}>
            {n(cobertura.naoResolvidos)} valor(es) não lido(s) sem executar o código
          </summary>
          <ul style={lista}>
            {pendentes.map((p, i) => (
              <li key={i}><code>{p.nome}</code> = <code>{p.valor}</code> — {p.arquivo}:{p.linha} ({p.motivo})</li>
            ))}
            {Number(cobertura.naoResolvidos) > pendentes.length && (
              <li>(+{n(Number(cobertura.naoResolvidos) - pendentes.length)} não listados)</li>
            )}
          </ul>
        </details>
      )}
    </div>
  );
}

// ─── Tokens com a origem ──────────────────────────────────────────────────────

function Origem({ origem }) {
  if (!origem) return <span style={{ color: C.warn }}>sem origem registrada</span>;
  if (origem.ajusteManual) {
    return <span style={{ color: C.quantum }}>ajuste manual{origem.valorAnterior ? ` (antes: ${origem.valorAnterior})` : ''}</span>;
  }
  return (
    <span title={origem.trecho}>
      {origem.arquivo}:{origem.linha}
      {origem.inferido ? ' · inferido' : ''}
      {origem.papelPor === 'ia' ? ` · papel indicado pela IA (${origem.nome})` : ''}
    </span>
  );
}

function TokensComOrigem({ ds }) {
  const origens = ds.repositorio?.origens ?? {};
  const linhas = GRUPOS.flatMap((g) => Object.entries(ds.canonical?.[g] ?? {})
    .map(([chave, valor]) => ({ g, chave, valor, origem: origens[`${g}.${chave}`] })));
  if (!linhas.length) return null;
  const td = { padding: '4px 8px', borderBottom: `1px solid ${C.blackBorder}`, verticalAlign: 'top' };
  return (
    <details style={{ marginTop: 10, fontSize: 12.5, color: C.neutral }}>
      <summary style={{ cursor: 'pointer' }}>
        Tokens e de onde saíram ({n(linhas.length)})
      </summary>
      <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%' }}>
        <caption style={{ textAlign: 'left', color: C.neutralDim, marginBottom: 4 }}>
          Origem de cada token no repositório
        </caption>
        <thead>
          <tr>
            <th scope="col" style={{ ...td, textAlign: 'left', color: C.neutralMuted }}>Token</th>
            <th scope="col" style={{ ...td, textAlign: 'left', color: C.neutralMuted }}>Valor</th>
            <th scope="col" style={{ ...td, textAlign: 'left', color: C.neutralMuted }}>Origem</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ g, chave, valor, origem }) => (
            <tr key={`${g}.${chave}`} data-testid={`origem-${g}.${chave}`}>
              <td style={td}>{GRUPO_LABEL[g]} · {chave}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>
                {g === 'color' && (
                  <span aria-hidden="true" style={{ display: 'inline-block', width: 11, height: 11,
                    marginRight: 6, borderRadius: 3, background: valor,
                    border: `1px solid ${C.blackBorder}`, verticalAlign: 'middle' }} />
                )}
                {valor}
              </td>
              <td style={{ ...td, color: C.neutralDim }}><Origem origem={origem} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function Componentes({ repositorio }) {
  const comps = repositorio?.componentes ?? [];
  const bps = repositorio?.breakpoints ?? [];
  if (!comps.length && !bps.length) return null;
  const porCategoria = {};
  comps.forEach((c) => { (porCategoria[c.categoria] ||= []).push(c); });
  return (
    <details style={{ marginTop: 6, fontSize: 12.5, color: C.neutral }}>
      <summary style={{ cursor: 'pointer' }}>
        Componentes reutilizáveis ({n(comps.length)}){bps.length ? ` e breakpoints (${n(bps.length)})` : ''}
      </summary>
      {bps.length > 0 && (
        <p style={{ margin: '6px 0 0', color: C.neutralDim }}>
          Breakpoints: {bps.map((b) => `${b.chave} = ${b.valor}${b.inferido ? ' (inferido)' : ''}`).join(' · ')}
        </p>
      )}
      {Object.entries(porCategoria).map(([cat, itens]) => (
        <div key={cat} style={{ marginTop: 6 }}>
          <span style={{ color: C.neutralDim }}>{CATEGORIA_LABEL[cat] ?? cat} ({n(itens.length)})</span>
          <ul style={{ ...lista, marginTop: 2 }}>
            {itens.map((c) => (
              <li key={`${c.repoId}:${c.arquivo}`}>
                {c.nome} <span style={{ color: C.neutralMuted, fontFamily: 'monospace' }}>— {c.arquivo}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </details>
  );
}

// ─── Ajustar tokens ───────────────────────────────────────────────────────────

function EditorDeTokens({ ds, onSalvar, onCancelar, salvando, erro }) {
  const uid = useId();
  const faltando = ds.repositorio?.cobertura?.papeisFaltando ?? [];
  const iniciais = {};
  GRUPOS.forEach((g) => Object.entries(ds.canonical?.[g] ?? {}).forEach(([k, v]) => { iniciais[`${g}.${k}`] = v; }));
  faltando.forEach((p) => { if (!(`color.${p}` in iniciais)) iniciais[`color.${p}`] = ''; });
  const [valores, setValores] = useState(iniciais);

  const mudancas = () => {
    const tokens = {};
    Object.entries(valores).forEach(([caminho, valor]) => {
      const antes = iniciais[caminho] ?? '';
      if (String(valor).trim() === String(antes).trim()) return;
      const [g, ...resto] = caminho.split('.');
      (tokens[g] ||= {})[resto.join('.')] = String(valor).trim() || null;
    });
    return tokens;
  };
  const tokens = mudancas();
  const mudou = Object.keys(tokens).length > 0;
  const campo = { background: C.black, color: C.neutral, border: `1px solid ${C.blackBorder}`,
    borderRadius: 6, padding: '5px 8px', fontSize: 12.5, fontFamily: 'monospace', width: '100%',
    boxSizing: 'border-box' };

  return (
    <form aria-label="Ajustar tokens do Design System"
      onSubmit={(e) => { e.preventDefault(); if (mudou) onSalvar(tokens); }}
      style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.blackBorder}` }}>
      <p style={{ margin: 0, fontSize: 12.5, color: C.neutralDim, lineHeight: 1.5, maxWidth: '78ch' }}>
        Corrija ou complete os tokens. Salvar grava uma nova versão; o token mexido passa a
        aparecer como “ajuste manual”. Campo vazio tira o token.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 10, marginTop: 10 }}>
        {Object.keys(valores).map((caminho) => {
          const [g, ...resto] = caminho.split('.');
          const chave = resto.join('.');
          const novo = !(iniciais[caminho]);
          return (
            <label key={caminho} htmlFor={`${uid}-${caminho}`} style={{ fontSize: 12, color: C.neutralDim }}>
              {GRUPO_LABEL[g]} · {chave}{novo ? ' (não declarado no repositório)' : ''}
              <input id={`${uid}-${caminho}`} value={valores[caminho]}
                onChange={(e) => setValores((v) => ({ ...v, [caminho]: e.target.value }))}
                style={campo} />
            </label>
          );
        })}
      </div>
      {erro && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>{mensagem(erro)}</p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <Btn size="sm" type="submit" disabled={!mudou || salvando}>
          {salvando ? 'Salvando…' : 'Salvar ajustes'}
        </Btn>
        <Btn variant="ghost" size="sm" onClick={onCancelar}>Cancelar</Btn>
      </div>
    </form>
  );
}

// ─── Painel ───────────────────────────────────────────────────────────────────

export function DesignSystemDoRepositorio({ projectId }) {
  const uid = useId();
  const { data: estado, isLoading, error: erroEstado } = useDesignSystemDoRepositorio(projectId);
  const extrair = useExtrairDoRepositorio(projectId);
  const activate = useActivateDesignSystem(projectId);
  const ajustar = useAjustarTokens(projectId);
  const [ajustando, setAjustando] = useState(false);
  const disparou = useRef(false);

  // Ao abrir: projeto existente sem Design System e sem extração anterior → extrai sozinho.
  const extrairAoAbrir = Boolean(estado?.aplicavel && estado?.extrairAoAbrir);
  useEffect(() => {
    if (extrairAoAbrir && !disparou.current) {
      disparou.current = true;
      extrair.mutate({});
    }
  }, [extrairAoAbrir]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !estado?.aplicavel) {
    return erroEstado
      ? <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>{mensagem(erroEstado)}</p>
      : null;
  }

  const resultado = extrair.data;
  // A extração recém-feita vale até o estado do servidor alcançá-la; depois disso o estado
  // manda (é ele que sabe se o DS foi ativado, ajustado ou deixado de usar).
  const ds = (estado.ds && (!resultado?.ds || estado.ds.version >= resultado.ds.version))
    ? estado.ds : (resultado?.ds ?? null);
  const emUso = Boolean(ds && ds.status === 'active');
  const semNada = !ds && resultado && resultado.encontrado === false;
  const repositorios = ds?.repositorio?.repositorios ?? resultado?.repositorios ?? estado.repositorios;
  const cobertura = ds?.repositorio?.cobertura ?? resultado?.cobertura;

  const salvar = (tokens) => {
    ajustar.mutate({ dsId: ds.id, tokens }, { onSuccess: () => { setAjustando(false); extrair.reset?.(); } });
  };

  return (
    <section aria-labelledby={`${uid}-titulo`} data-testid="ds-do-repositorio"
      style={{ marginTop: 12, padding: 14, borderRadius: 10,
        border: `1px solid ${emUso ? C.cyber : C.blackBorder}`,
        background: emUso ? 'rgba(1,230,228,0.05)' : C.blackElev2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h4 id={`${uid}-titulo`} style={{ margin: 0, fontSize: 14, color: C.neutral }}>
          Design System do repositório
        </h4>
        <span style={etiqueta(C.cyber)}>principal</span>
        {emUso && <span style={etiqueta(C.ok)}>selecionado</span>}
        {ds && !emUso && <span style={etiqueta(C.warn)}>fora de uso</span>}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 12.5, color: C.neutralDim, lineHeight: 1.5, maxWidth: '82ch' }}>
        Projeto existente: as telas usam as cores, a tipografia e os componentes que já estão no
        front-end do repositório indexado. As outras opções continuam disponíveis como alternativa.
      </p>

      {extrair.isPending && (
        <p role="status" aria-live="polite" style={{ margin: '10px 0 0', fontSize: 13, color: C.neutral }}>
          Lendo o front-end do repositório indexado e extraindo o Design System…
        </p>
      )}

      {extrair.error && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>{mensagem(extrair.error)}</p>
      )}

      {semNada && (
        <div role="status" aria-live="polite" data-testid="ds-repo-sem-nada"
          style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
            border: `1px solid ${C.warn}66`, background: 'rgba(255,176,32,0.08)' }}>
          <strong style={{ color: C.warn, fontSize: 13 }}>Nada para extrair</strong>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: C.neutral }}>{resultado.motivo}</p>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: C.neutralDim }}>
            {n(resultado.cobertura?.arquivosDeFront)} arquivo(s) de front-end no índice
            {' · '}{n(resultado.cobertura?.arquivosLidos)} arquivo(s) de tema/tokens lido(s).
            Use uma das opções abaixo.
          </p>
        </div>
      )}

      {!ds && !extrair.isPending && !semNada && (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: C.neutralDim }}>
          Ainda não extraído.
        </p>
      )}

      {ds && (
        <>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: C.neutralDim }}>
            <strong style={{ color: C.neutral }}>Extraído do repositório</strong>
            {' · '}versão {ds.version}
            {' · '}modo {ds.mode === 'enforce' ? 'obrigatório' : 'orientativo'}
          </p>
          {ds.repositorio?.descricao && (
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: C.neutral, maxWidth: '82ch' }}>
              {ds.repositorio.descricao}
            </p>
          )}
          <Indice repositorios={repositorios} />
          <Cobertura cobertura={cobertura} />
          <TokensComOrigem ds={ds} />
          <Componentes repositorio={ds.repositorio} />
        </>
      )}

      {!ds && <Indice repositorios={repositorios} />}

      {ajustando && ds ? (
        <EditorDeTokens key={ds.id} ds={ds} onSalvar={salvar} onCancelar={() => setAjustando(false)}
          salvando={ajustar.isPending} erro={ajustar.error} />
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {ds && !emUso && (
            <Btn size="sm" onClick={() => activate.mutate(ds.id, { onSuccess: () => extrair.reset?.() })}
              disabled={activate.isPending}>
              {activate.isPending ? 'Ativando…' : 'Usar o Design System do repositório'}
            </Btn>
          )}
          <Btn variant="outline" size="sm" onClick={() => extrair.mutate({})} disabled={extrair.isPending}
            title="Lê de novo o repositório indexado (use depois de reindexar)">
            {extrair.isPending ? 'Extraindo…' : (ds ? 'Extrair de novo' : 'Extrair do repositório')}
          </Btn>
          {ds && (
            <Btn variant="outline" size="sm" onClick={() => setAjustando(true)}>Ajustar tokens</Btn>
          )}
        </div>
      )}

      {activate.error && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>{mensagem(activate.error)}</p>
      )}
    </section>
  );
}

export default DesignSystemDoRepositorio;
